import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Landmark, Plus, X, Loader2, Trash2, Upload, Calculator, ArrowLeft, FileSpreadsheet, RefreshCw, Building2, FileText, ShieldCheck, Wallet, PiggyBank, SlidersHorizontal, Percent, TrendingDown, Check, LineChart, MoreVertical, Ban, Play, Coins, Pencil, Download } from "lucide-react";
import api from "../../lib/api";
import { LoansGuide } from "../../components/guides/LoansGuide";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../../components/ui/dropdown-menu";
import { GuaranteeFields, GuaranteeBadge, GuaranteeModal } from "../../components/loans/LoanGuarantee";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const fmt = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const monthLabel = (r) => (r.month_label ? r.month_label : (r.month >= 1 && r.month <= 12 ? MONTHS_FR[r.month - 1] : "")) + (r.year ? ` ${r.year}` : "");

function buildCapitalTimeline(loans) {
  const withSched = loans.filter((l) => (l.schedule || []).length);
  if (!withSched.length) return [];
  const keys = new Set();
  const info = withSched.map((l) => {
    const m = {};
    (l.schedule || []).forEach((r) => { if (r.date) { const k = r.date.slice(0, 7); m[k] = r.balance; keys.add(k); } });
    return m;
  });
  return [...keys].sort().map((k) => {
    let total = 0;
    info.forEach((m) => { if (k in m) total += m[k] || 0; });
    const [y, mm] = k.split("-");
    return { key: k, label: `${mm}/${y}`, capital: Math.round(total) };
  });
}

export default function Prets() {
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [types, setTypes] = useState([]);
  const [deferTypes, setDeferTypes] = useState([]);
  const [modal, setModal] = useState(null);
  const [simLoan, setSimLoan] = useState(null);
  const [insLoan, setInsLoan] = useState(null);
  const [guarLoan, setGuarLoan] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [regenId, setRegenId] = useState(null);
  const [selectedEch, setSelectedEch] = useState(null); // { loanId, no }
  const [prepayLoan, setPrepayLoan] = useState(null);
  const [editEch, setEditEch] = useState(null); // { loan, row }

  const loadLoans = () => api.get("/loans").then((r) => setLoans(r.data || [])).catch(() => {});
  const loadBenef = () => api.get("/loan-beneficiaries").then((r) => setBeneficiaries(r.data || [])).catch(() => {});
  useEffect(() => {
    loadLoans();
    loadBenef();
    api.get("/owners").then((r) => setOwners(r.data || [])).catch(() => {});
    api.get("/properties").then((r) => setProperties(r.data || [])).catch(() => {});
    api.get("/loans/types").then((r) => { setTypes(r.data.types || []); setDeferTypes(r.data.defer_types || []); }).catch(() => {});
  }, []);

  const del = async (loan) => {
    if (!window.confirm("Supprimer ce prêt et ses dépenses générées en comptabilité ?")) return;
    try {
      const res = await api.delete(`/loans/${loan.id}`);
      setLoans((p) => p.filter((l) => l.id !== loan.id));
      toast.success(`Prêt supprimé (${res.data.removed_expenses} dépense(s) retirée(s))`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const regen = async (loan) => {
    setRegenId(loan.id);
    try {
      const res = await api.post(`/loans/${loan.id}/generate-expenses`);
      await loadLoans();
      toast.success(res.data.created > 0 ? `${res.data.created} ligne(s) de dépense générée(s)` : "Dépenses déjà à jour");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setRegenId(null); }
  };

  const exportLoan = (loan, format) => {
    window.open(`${BACKEND_URL}/api/loans/${loan.id}/export?format=${format}`, "_blank");
  };

  const toggleStop = async (loan) => {
    try {
      await api.post(`/loans/${loan.id}/stop`, { stopped: !loan.stopped });
      await loadLoans();
      toast.success(loan.stopped ? "Prêt réactivé — échéances régénérées" : "Prêt gelé — plus aucune écriture générée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { month: "short", year: "numeric" }) : "—");
  const durationLabel = (m) => { if (!m) return "—"; const y = Math.floor(m / 12), r = m % 12; return [y ? `${y} an${y > 1 ? "s" : ""}` : "", r ? `${r} mois` : ""].filter(Boolean).join(" ") || `${m} mois`; };

  const sum = (fn) => loans.reduce((s, l) => s + (fn(l) || 0), 0);
  const totalRemaining = sum((l) => l.remaining_capital);
  const totalCost = sum((l) => (l.totals || {}).total_payment);
  const totalAnnualInsurance = sum((l) => l.annual_insurance);
  const timeline = buildCapitalTimeline(loans);

  return (
    <div className="space-y-6" data-testid="prets-page">
      <div className="flex justify-end -mb-3"><LoansGuide /></div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => navigate("/app/finances")} className="inline-flex items-center gap-2 text-sm text-brand-green/60 hover:text-brand-greenDeep font-medium" data-testid="prets-back">
          <ArrowLeft className="w-4 h-4" /> Retour aux finances
        </button>
        <button onClick={() => setModal({})} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm transition-colors" data-testid="add-loan-btn">
          <Plus className="w-4 h-4" /> Ajouter un prêt
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><Landmark className="w-5 h-5" /></div>
        <div>
          <h2 className="font-display text-2xl font-semibold text-brand-greenDeep">Prêts immobiliers</h2>
          <p className="text-sm text-brand-green/55">Importez ou calculez vos tableaux d'amortissement — les échéances alimentent automatiquement la comptabilité.</p>
        </div>
      </div>

      {loans.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-4" data-testid="loans-summary">
          <div className="bg-brand-greenDeep text-white rounded-2xl p-5" data-testid="summary-remaining">
            <div className="flex items-center gap-2 text-white/70 text-xs font-medium"><Wallet className="w-4 h-4" /> Capital restant dû (total)</div>
            <p className="mt-2 font-display text-2xl font-semibold">{fmt(totalRemaining)} €</p>
            <p className="text-white/50 text-xs mt-1">{loans.length} prêt(s) en cours</p>
          </div>
          <div className="bg-white border border-brand-soft rounded-2xl p-5" data-testid="summary-cost">
            <div className="flex items-center gap-2 text-brand-green/60 text-xs font-medium"><PiggyBank className="w-4 h-4" /> Coût total (tous prêts)</div>
            <p className="mt-2 font-display text-2xl font-semibold text-brand-greenDeep">{fmt(totalCost)} €</p>
            <p className="text-brand-green/45 text-xs mt-1">capital + intérêts + assurance</p>
          </div>
          <div className="bg-white border border-brand-soft rounded-2xl p-5" data-testid="summary-insurance">
            <div className="flex items-center gap-2 text-brand-green/60 text-xs font-medium"><ShieldCheck className="w-4 h-4" /> Assurance emprunteur / an</div>
            <p className="mt-2 font-display text-2xl font-semibold text-brand-greenDeep">{fmt(totalAnnualInsurance)} €</p>
            <p className="text-brand-green/45 text-xs mt-1">à comparer chaque année pour renégocier</p>
          </div>
        </div>
      )}

      {timeline.length > 1 && (
        <div className="bg-white rounded-2xl border border-brand-soft p-5" data-testid="capital-timeline-chart">
          <div className="flex items-center gap-2 mb-4">
            <LineChart className="w-4 h-4 text-brand-greenDeep" />
            <h3 className="font-semibold text-brand-greenDeep text-sm">Capital restant dû — évolution (tous prêts)</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={timeline} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="capGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#c9a24b" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#c9a24b" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#efe7d6" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b6b60" }} interval="preserveStartEnd" minTickGap={40} />
              <YAxis tick={{ fontSize: 11, fill: "#6b6b60" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={44} />
              <Tooltip formatter={(v) => [`${(v || 0).toLocaleString("fr-FR")} €`, "Capital restant dû"]} labelStyle={{ color: "#121311" }} contentStyle={{ borderRadius: 12, border: "1px solid #efe7d6", fontSize: 12 }} />
              <Area type="monotone" dataKey="capital" stroke="#b28a37" strokeWidth={2} fill="url(#capGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {loans.length === 0 && (
        <div className="bg-white rounded-2xl border border-brand-soft p-10 text-center" data-testid="prets-empty">
          <Landmark className="w-8 h-8 text-brand-green/30 mx-auto mb-3" />
          <p className="text-brand-green/60">Aucun prêt enregistré. Ajoutez votre premier prêt pour générer les échéances.</p>
        </div>
      )}

      {loans.length > 0 && (
        <div className="bg-white rounded-2xl border border-brand-soft overflow-hidden" data-testid="loans-recap-table">
          <div className="px-5 py-4 border-b border-brand-soft flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-brand-greenDeep" />
              <h3 className="font-semibold text-brand-greenDeep text-sm">Récapitulatif des prêts</h3>
            </div>
            <button onClick={() => window.open(`${BACKEND_URL}/api/loans/recap-export`, "_blank")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-soft hover:bg-brand-mint text-brand-greenDeep font-semibold text-xs transition-colors" data-testid="loans-recap-export-btn">
              <FileSpreadsheet className="w-3.5 h-3.5" /> Exporter en Excel
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-softer text-left text-brand-green/60 text-xs">
                <tr>
                  <th className="px-4 py-3 font-medium">Bien</th>
                  <th className="px-4 py-3 font-medium">Organisme</th>
                  <th className="px-4 py-3 font-medium">Garantie</th>
                  <th className="px-4 py-3 font-medium text-right">Montant d'origine</th>
                  <th className="px-4 py-3 font-medium">Début</th>
                  <th className="px-4 py-3 font-medium">Fin</th>
                  <th className="px-4 py-3 font-medium">Durée</th>
                  <th className="px-4 py-3 font-medium text-right">Échéances payées</th>
                  <th className="px-4 py-3 font-medium text-right">Restant dû</th>
                  <th className="px-4 py-3 font-medium text-right">Total</th>
                  <th className="px-4 py-3 font-medium text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-soft">
                {loans.map((loan) => {
                  const t = loan.totals || {};
                  const open = expanded === loan.id;
                  const ml = loan.months_left ?? 0;
                  const ended = ml === 0 && (t.count || 0) > 0;
                  const rowSelected = selectedEch && selectedEch.loanId === loan.id;
                  return (
                    <React.Fragment key={loan.id}>
                      <tr className="text-brand-green/80 hover:bg-brand-softer/40" data-testid={`loan-row-${loan.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 font-medium text-brand-greenDeep">
                            {loan.property && loan.property !== "Pas lié à un bien" ? <><Building2 className="w-3.5 h-3.5 text-brand-green/40" /> {loan.property}</> : <span className="text-brand-green/50">Non affecté</span>}
                          </div>
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className="inline-flex px-2 py-0.5 rounded-full bg-brand-lime/30 text-brand-greenDeep text-[10px] font-semibold">{loan.type?.replace("Prêt : ", "")}</span>
                            {loan.stopped && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold" data-testid={`loan-frozen-${loan.id}`}><Ban className="w-2.5 h-2.5" /> Gelé</span>}
                            {ended && !loan.stopped && <span className="inline-flex px-2 py-0.5 rounded-full bg-brand-emerald/10 text-brand-emerald text-[10px] font-semibold">Soldé</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">{loan.payer_to || "—"}</td>
                        <td className="px-4 py-3" data-testid={`loan-guarantee-cell-${loan.id}`}>{loan.guarantee ? <GuaranteeBadge loan={loan} /> : <button onClick={() => setGuarLoan(loan)} className="text-xs text-brand-green/40 hover:text-brand-greenDeep underline-offset-2 hover:underline" data-testid={`loan-guarantee-add-${loan.id}`}>À renseigner</button>}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">{fmt(loan.original_amount)} €</td>
                        <td className="px-4 py-3 whitespace-nowrap capitalize">{fmtDate(loan.first_date)}</td>
                        <td className="px-4 py-3 whitespace-nowrap capitalize">{fmtDate(loan.last_date)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{durationLabel(loan.duration_months)}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">{fmt(loan.paid_sum)} €<span className="block text-[10px] text-brand-green/40">{loan.generated_count}/{t.count} éch.</span></td>
                        <td className="px-4 py-3 text-right whitespace-nowrap font-semibold text-brand-greenDeep">{fmt(loan.remaining_capital)} €</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap font-semibold">{fmt(loan.total_amount)} €</td>
                        <td className="px-4 py-3 text-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1.5 rounded-full hover:bg-brand-soft text-brand-green/60 hover:text-brand-greenDeep" title="Actions" data-testid={`loan-action-${loan.id}`}><MoreVertical className="w-4 h-4" /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-60">
                              <DropdownMenuItem onClick={() => { setExpanded(open ? null : loan.id); setSelectedEch(null); }} data-testid={`action-schedule-${loan.id}`}>
                                <FileText className="w-4 h-4 mr-2" /> {open ? "Masquer le plan d'amortissement" : "Plan d'amortissement"}
                              </DropdownMenuItem>
                              {open && (
                                <DropdownMenuItem disabled={!(rowSelected && selectedEch.no != null)} onClick={() => { const row = (loan.schedule || []).find((r) => r.no === selectedEch.no); if (row) setEditEch({ loan, row }); }} data-testid={`action-edit-ech-${loan.id}`}>
                                  <Pencil className="w-4 h-4 mr-2" /> {rowSelected ? `Modifier l'échéance n°${selectedEch.no}` : "Modifier une échéance (sélectionnez)"}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => setPrepayLoan(loan)} data-testid={`action-prepay-${loan.id}`}>
                                <Coins className="w-4 h-4 mr-2" /> Remboursement anticipé
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => toggleStop(loan)} data-testid={`action-stop-${loan.id}`}>
                                {loan.stopped ? <><Play className="w-4 h-4 mr-2" /> Réactiver le prêt</> : <><Ban className="w-4 h-4 mr-2" /> Arrêter les échéances</>}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => exportLoan(loan, "pdf")}><FileText className="w-4 h-4 mr-2" /> Exporter en PDF</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => exportLoan(loan, "xlsx")}><FileSpreadsheet className="w-4 h-4 mr-2" /> Exporter en Excel</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setInsLoan(loan)}><Percent className="w-4 h-4 mr-2" /> Comparer l'assurance</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setGuarLoan(loan)} data-testid={`action-guarantee-${loan.id}`}><ShieldCheck className="w-4 h-4 mr-2" /> Garantie du prêt{loan.guarantee ? "" : " (à renseigner)"}</DropdownMenuItem>
                              {loan.params && <DropdownMenuItem onClick={() => setSimLoan(loan)}><SlidersHorizontal className="w-4 h-4 mr-2" /> Simuler (modulation)</DropdownMenuItem>}
                              <DropdownMenuItem onClick={() => regen(loan)} disabled={regenId === loan.id}><RefreshCw className="w-4 h-4 mr-2" /> Régénérer les dépenses</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => del(loan)} className="text-red-600 focus:text-red-600" data-testid={`action-delete-${loan.id}`}><Trash2 className="w-4 h-4 mr-2" /> Supprimer le prêt</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={11} className="p-0 bg-brand-softer/30">
                            <div className="px-4 py-2 text-xs text-brand-green/55 flex items-center gap-1.5"><Pencil className="w-3 h-3" /> Cliquez sur une échéance pour la sélectionner, puis Action → « Modifier l'échéance ».</div>
                            <ScheduleTable schedule={loan.schedule || []} totals={t} selectedNo={rowSelected ? selectedEch.no : null} onSelect={(no) => setSelectedEch({ loanId: loan.id, no })} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="font-semibold text-brand-greenDeep bg-brand-lime/20 border-t-2 border-brand-lime" data-testid="loans-recap-total-row">
                  <td className="px-4 py-3" colSpan={3}>Totaux ({loans.length} prêt{loans.length > 1 ? "s" : ""})</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap" data-testid="total-original">{fmt(sum((l) => l.original_amount))} €</td>
                  <td className="px-4 py-3" colSpan={3}></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap" data-testid="total-paid">{fmt(sum((l) => l.paid_sum))} €</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap" data-testid="total-remaining">{fmt(totalRemaining)} €</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap" data-testid="total-total">{fmt(sum((l) => l.total_amount))} €</td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {modal !== null && (
        <LoanModal owners={owners} properties={properties} beneficiaries={beneficiaries} types={types} deferTypes={deferTypes}
          onBenefCreated={loadBenef}
          onClose={() => setModal(null)}
          onSaved={() => { loadLoans(); loadBenef(); setModal(null); }} />
      )}
      {simLoan && <SimulateModal loan={simLoan} onClose={() => setSimLoan(null)} onApplied={() => { loadLoans(); setSimLoan(null); }} />}
      {insLoan && <InsuranceCompareModal loan={insLoan} onClose={() => setInsLoan(null)} onApplied={() => { loadLoans(); setInsLoan(null); }} />}
      {guarLoan && <GuaranteeModal loan={guarLoan} onClose={() => setGuarLoan(null)} onSaved={(nl) => setLoans((p) => p.map((l) => (l.id === nl.id ? { ...l, ...nl } : l)))} />}
      {prepayLoan && <PrepayModal loan={prepayLoan} onClose={() => setPrepayLoan(null)} onApplied={() => { loadLoans(); setPrepayLoan(null); }} />}
      {editEch && <EditEcheanceModal loan={editEch.loan} row={editEch.row} onClose={() => setEditEch(null)} onSaved={() => { loadLoans(); setEditEch(null); setSelectedEch(null); }} />}
      <style>{`.inp{width:100%;padding:0.55rem 0.85rem;border-radius:0.8rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.85rem}.inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}`}</style>
    </div>
  );
}

function ScheduleTable({ schedule, totals, selectedNo = null, onSelect = null }) {
  return (
    <div className="border-t border-brand-soft" data-testid="schedule-table">
      <div className="overflow-x-auto max-h-96">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-brand-softer">
            <tr className="text-left text-brand-green/60">
              <th className="px-4 py-2 font-medium">N°</th>
              <th className="px-4 py-2 font-medium">Échéance</th>
              <th className="px-4 py-2 font-medium text-right">Mensualité (assur.)</th>
              <th className="px-4 py-2 font-medium text-right">Intérêts</th>
              <th className="px-4 py-2 font-medium text-right">Assurance</th>
              <th className="px-4 py-2 font-medium text-right">Capital remb.</th>
              <th className="px-4 py-2 font-medium text-right">Capital restant dû</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-soft">
            {schedule.map((r, i) => {
              const sel = selectedNo != null && r.no === selectedNo;
              return (
                <tr key={i}
                  onClick={onSelect ? () => onSelect(r.no) : undefined}
                  className={`text-brand-green/80 ${onSelect ? "cursor-pointer" : ""} ${sel ? "bg-brand-lime/40 ring-1 ring-brand-lime" : r.generated ? "hover:bg-brand-softer/50" : "bg-amber-50/40 hover:bg-amber-50"}`}
                  data-testid={onSelect ? `ech-row-${r.no}` : undefined}>
                  <td className="px-4 py-1.5 font-medium">{r.no}{r.prepayment ? <span className="ml-1 text-brand-emerald" title="Remboursement anticipé">•</span> : null}</td>
                  <td className="px-4 py-1.5 whitespace-nowrap">{monthLabel(r)}</td>
                  <td className="px-4 py-1.5 text-right">{fmt(r.payment_with_insurance)}{r.extra ? <span className="text-brand-emerald"> +{fmt(r.extra)}</span> : null}</td>
                  <td className="px-4 py-1.5 text-right">{fmt(r.interest)}</td>
                  <td className="px-4 py-1.5 text-right">{fmt(r.insurance)}</td>
                  <td className="px-4 py-1.5 text-right">{fmt(r.principal)}</td>
                  <td className="px-4 py-1.5 text-right">{fmt(r.balance)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="font-semibold text-brand-greenDeep bg-brand-lime/20">
              <td className="px-4 py-2" colSpan={2}>Totaux</td>
              <td className="px-4 py-2 text-right">{fmt(totals.total_payment)}</td>
              <td className="px-4 py-2 text-right">{fmt(totals.total_interest)}</td>
              <td className="px-4 py-2 text-right">{fmt(totals.total_insurance)}</td>
              <td className="px-4 py-2 text-right">{fmt(totals.total_principal)}</td>
              <td className="px-4 py-2 text-right">—</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function PrepayModal({ loan, onClose, onApplied }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [total, setTotal] = useState(false);
  const [mode, setMode] = useState("reduce_duration"); // reduce_duration | reduce_payment
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!date) { toast.error("Date requise"); return; }
    if (!total && (!amount || Number(amount) <= 0)) { toast.error("Montant requis pour un remboursement partiel"); return; }
    setLoading(true);
    try {
      await api.post(`/loans/${loan.id}/prepay`, { date, amount: Number(amount) || 0, total, reduce_duration: mode === "reduce_duration" });
      toast.success(total ? "Prêt soldé" : "Remboursement anticipé appliqué");
      onApplied();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="prepay-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold text-brand-greenDeep flex items-center gap-2"><Coins className="w-5 h-5" /> Remboursement anticipé</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-brand-soft"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-brand-green/55 mb-4">{loan.property || "Non affecté"} · Capital restant dû : <b>{fmt(loan.remaining_capital)} €</b></p>
        <label className="block mb-3"><span className="block text-sm font-medium text-brand-green/70 mb-1.5">Date du remboursement</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="inp" data-testid="prepay-date" /></label>
        <label className="flex items-center gap-2 mb-3 text-sm text-brand-green/80">
          <input type="checkbox" checked={total} onChange={(e) => setTotal(e.target.checked)} data-testid="prepay-total" /> Solder totalement le prêt
        </label>
        {!total && (
          <>
            <label className="block mb-3"><span className="block text-sm font-medium text-brand-green/70 mb-1.5">Montant remboursé (€)</span>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="inp" placeholder="ex. 10000" data-testid="prepay-amount" /></label>
            <div className="mb-4">
              <span className="block text-sm font-medium text-brand-green/70 mb-1.5">Recalcul du tableau</span>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setMode("reduce_duration")} className={`px-3 py-2 rounded-xl text-xs font-semibold border ${mode === "reduce_duration" ? "bg-brand-lime/40 border-brand-lime text-brand-greenDeep" : "border-brand-soft text-brand-green/60"}`} data-testid="prepay-mode-duration">Réduire la durée</button>
                <button onClick={() => setMode("reduce_payment")} className={`px-3 py-2 rounded-xl text-xs font-semibold border ${mode === "reduce_payment" ? "bg-brand-lime/40 border-brand-lime text-brand-greenDeep" : "border-brand-soft text-brand-green/60"}`} data-testid="prepay-mode-payment">Réduire la mensualité</button>
              </div>
            </div>
          </>
        )}
        <button onClick={submit} disabled={loading} className="w-full py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2" data-testid="prepay-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Appliquer
        </button>
      </div>
    </div>
  );
}

function EditEcheanceModal({ loan, row, onClose, onSaved }) {
  const [f, setF] = useState({
    payment_with_insurance: row.payment_with_insurance ?? 0,
    interest: row.interest ?? 0,
    insurance: row.insurance ?? 0,
    principal: row.principal ?? 0,
    balance: row.balance ?? 0,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    setLoading(true);
    try {
      await api.put(`/loans/${loan.id}/echeance/${row.no}`, {
        payment_with_insurance: Number(f.payment_with_insurance) || 0,
        interest: Number(f.interest) || 0,
        insurance: Number(f.insurance) || 0,
        principal: Number(f.principal) || 0,
        balance: Number(f.balance) || 0,
      });
      toast.success(`Échéance n°${row.no} modifiée`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  const Field = ({ label, k }) => (
    <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">{label}</span>
      <input type="number" step="0.01" value={f[k]} onChange={(e) => set(k, e.target.value)} className="inp" data-testid={`edit-ech-${k}`} /></label>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="edit-ech-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold text-brand-greenDeep flex items-center gap-2"><Pencil className="w-5 h-5" /> Modifier l'échéance n°{row.no}</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-brand-soft"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-brand-green/55 mb-4">{monthLabel(row)} · La modification ne recalcule pas les échéances suivantes.</p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Field label="Mensualité (assur.)" k="payment_with_insurance" />
          <Field label="Intérêts" k="interest" />
          <Field label="Assurance" k="insurance" />
          <Field label="Capital remboursé" k="principal" />
          <Field label="Capital restant dû" k="balance" />
        </div>
        <button onClick={submit} disabled={loading} className="w-full py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2" data-testid="edit-ech-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enregistrer
        </button>
      </div>
    </div>
  );
}

function LoanModal({ owners, properties, beneficiaries, types, deferTypes, onBenefCreated, onClose, onSaved }) {
  const [meta, setMeta] = useState({ owner: "", property: "", type: types[0] || "Prêt : Immobilier", start_month: new Date().toISOString().slice(0, 7), payer_to: "", description: "", guarantee: "", guarantee_detail: "" });
  const [mode, setMode] = useState("calcul"); // "calcul" | "import" | "simple"
  const [simple, setSimple] = useState({ amount: "", monthly: "", months: "", start: new Date().toISOString().slice(0, 7) });
  const [params, setParams] = useState({ amount: "", months: "", rate: "", insurance_rate: "", defer_type: 1, defer_months: "" });
  const [schedule, setSchedule] = useState(null);
  const [totals, setTotals] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newBenef, setNewBenef] = useState(false);
  const [benefName, setBenefName] = useState("");

  const set = (k, v) => setMeta((s) => ({ ...s, [k]: v }));
  const setP = (k, v) => setParams((s) => ({ ...s, [k]: v }));

  const addBenef = async () => {
    if (!benefName.trim()) return;
    try {
      const res = await api.post("/loan-beneficiaries", { name: benefName.trim() });
      set("payer_to", res.data.name);
      setNewBenef(false); setBenefName("");
      onBenefCreated();
      toast.success("Bénéficiaire créé");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const doSimple = () => {
    const amount = Number(simple.amount), monthly = Number(simple.monthly), months = Number(simple.months);
    if (!amount || amount <= 0) return toast.error("Montant emprunté requis");
    if (!monthly || monthly <= 0) return toast.error("Mensualité requise");
    if (!months || months <= 0) return toast.error("Durée en mois requise");
    const [y, m] = (simple.start || "").split("-").map(Number);
    const capitalPerMonth = amount / months;
    const sched = [];
    let balance = amount;
    for (let i = 0; i < months; i++) {
      const d = new Date((y || new Date().getFullYear()), (m || 1) - 1 + i, 1);
      const cap = Math.min(capitalPerMonth, balance);
      balance = Math.max(0, balance - cap);
      const interet = Math.max(0, monthly - cap);
      sched.push({
        no: i + 1, year: d.getFullYear(), month: d.getMonth() + 1, generated: true,
        payment: Math.round((monthly) * 100) / 100, payment_with_insurance: Math.round(monthly * 100) / 100,
        interest: Math.round(interet * 100) / 100, insurance: 0,
        principal: Math.round(cap * 100) / 100, balance: Math.round(balance * 100) / 100,
      });
    }
    const totals = sched.reduce((a, r) => ({
      total_payment: a.total_payment + r.payment_with_insurance, total_interest: a.total_interest + r.interest,
      total_insurance: a.total_insurance + r.insurance, total_principal: a.total_principal + r.principal, count: a.count + 1,
    }), { total_payment: 0, total_interest: 0, total_insurance: 0, total_principal: 0, count: 0 });
    setSchedule(sched); setTotals(totals);
    toast.success(`${sched.length} échéances générées (mensualité fixe)`);
  };

  const doCalculate = async () => {
    const [y, m] = (meta.start_month || "").split("-");
    setBusy(true);
    try {
      const res = await api.post("/loans/calculate", {
        amount: Number(params.amount), months: Number(params.months), rate: Number(params.rate),
        insurance_rate: Number(params.insurance_rate), defer_type: Number(params.defer_type),
        defer_months: Number(params.defer_months || 0), start_year: Number(y) || new Date().getFullYear(), start_month: Number(m) || 1,
      });
      setSchedule(res.data.schedule); setTotals(res.data.totals);
      toast.success(`${res.data.schedule.length} échéances calculées`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de calcul"); }
    finally { setBusy(false); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/loans/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSchedule(res.data.schedule); setTotals(res.data.totals);
      toast.success(`${res.data.schedule.length} échéances importées`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Import impossible"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!schedule || !schedule.length) return toast.error("Calculez ou importez d'abord le tableau d'amortissement");
    const startMonth = mode === "simple" ? simple.start : meta.start_month;
    const [y, m] = (startMonth || "").split("-");
    setBusy(true);
    try {
      const payload = {
        owner: meta.owner, property: meta.property, type: meta.type,
        start_date: startMonth ? `${startMonth}-01` : "",
        payer_to: meta.payer_to, description: meta.description, guarantee: meta.guarantee, guarantee_detail: meta.guarantee_detail, source: mode, schedule,
      };
      if (mode === "calcul") payload.params = {
        amount: Number(params.amount), months: Number(params.months), rate: Number(params.rate),
        insurance_rate: Number(params.insurance_rate), defer_type: Number(params.defer_type),
        defer_months: Number(params.defer_months || 0), start_year: Number(y) || new Date().getFullYear(), start_month: Number(m) || 1,
      };
      const res = await api.post("/loans", payload);
      toast.success(`Prêt enregistré — ${res.data.generated_count} échéance(s) comptabilisée(s)`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const deferred = Number(params.defer_type) !== 1;

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="loan-modal">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><Landmark className="w-5 h-5" /> Nouveau prêt</h3>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          {/* Informations du prêt */}
          <div className="grid sm:grid-cols-2 gap-3">
            <L label="Propriétaire">
              <select value={meta.owner} onChange={(e) => set("owner", e.target.value)} className="inp" data-testid="loan-owner">
                <option value="">— Sélectionner —</option>
                {owners.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
              </select>
            </L>
            <L label="Bien">
              <select value={meta.property} onChange={(e) => set("property", e.target.value)} className="inp" data-testid="loan-property">
                <option value="">— Sélectionner —</option>
                {properties.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </L>
            <L label="Type de prêt">
              <select value={meta.type} onChange={(e) => set("type", e.target.value)} className="inp" data-testid="loan-type">
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </L>
            <L label="Début du paiement (mois / année)">
              <input type="month" value={meta.start_month} onChange={(e) => set("start_month", e.target.value)} className="inp" data-testid="loan-start" />
            </L>
            <L label="Payer à">
              {!newBenef ? (
                <div className="flex gap-2">
                  <select value={meta.payer_to} onChange={(e) => { if (e.target.value === "__new__") { setNewBenef(true); } else set("payer_to", e.target.value); }} className="inp" data-testid="loan-payer">
                    <option value="">— Sélectionner —</option>
                    {beneficiaries.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
                    <option value="__new__">+ Créer un nouveau bénéficiaire</option>
                  </select>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input value={benefName} onChange={(e) => setBenefName(e.target.value)} placeholder="Ex. BNP, Société Générale…" className="inp" data-testid="loan-new-benef-input" />
                  <button onClick={addBenef} className="px-3 rounded-xl bg-brand-lime text-brand-greenDeep font-semibold text-sm" data-testid="loan-new-benef-save">OK</button>
                  <button onClick={() => setNewBenef(false)} className="px-2 text-brand-green/50"><X className="w-4 h-4" /></button>
                </div>
              )}
            </L>
            <L label="Description">
              <input value={meta.description} onChange={(e) => set("description", e.target.value)} placeholder="Décrire le prêt" className="inp" data-testid="loan-description" />
            </L>
          </div>
          <GuaranteeFields value={meta.guarantee} detail={meta.guarantee_detail} onChange={(g) => setMeta((m) => ({ ...m, ...g }))} />

          {/* Mode: calcul / import */}
          <div className="flex items-center gap-1 bg-brand-softer rounded-full p-1 w-fit" data-testid="loan-mode-tabs">
            <button onClick={() => { setMode("calcul"); setSchedule(null); }} className={`px-4 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 ${mode === "calcul" ? "bg-white text-brand-greenDeep shadow-sm" : "text-brand-green/55"}`} data-testid="loan-mode-calcul"><Calculator className="w-4 h-4" /> Calculer</button>
            <button onClick={() => { setMode("import"); setSchedule(null); }} className={`px-4 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 ${mode === "import" ? "bg-white text-brand-greenDeep shadow-sm" : "text-brand-green/55"}`} data-testid="loan-mode-import"><FileSpreadsheet className="w-4 h-4" /> Importer un Excel</button>
            <button onClick={() => { setMode("simple"); setSchedule(null); }} className={`px-4 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 ${mode === "simple" ? "bg-white text-brand-greenDeep shadow-sm" : "text-brand-green/55"}`} data-testid="loan-mode-simple"><Calculator className="w-4 h-4" /> Simplifiée</button>
          </div>

          {mode === "simple" && (
            <div className="rounded-2xl bg-brand-softer/60 p-4 space-y-3" data-testid="loan-simple-panel">
              <p className="text-xs text-brand-green/55">Le plan d'amortissement est généré en appliquant la mensualité indiquée sur toute la durée du prêt.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <L label="Montant emprunté (€)"><input type="number" step="0.01" value={simple.amount} onChange={(e) => setSimple((s) => ({ ...s, amount: e.target.value }))} className="inp" data-testid="loan-simple-amount" /></L>
                <L label="Mensualité (€)"><input type="number" step="0.01" value={simple.monthly} onChange={(e) => setSimple((s) => ({ ...s, monthly: e.target.value }))} className="inp" data-testid="loan-simple-monthly" /></L>
                <L label="Durée (mois)"><input type="number" value={simple.months} onChange={(e) => setSimple((s) => ({ ...s, months: e.target.value }))} className="inp" data-testid="loan-simple-months" /></L>
                <L label="Date de début (mois/année)"><input type="month" value={simple.start} onChange={(e) => setSimple((s) => ({ ...s, start: e.target.value }))} className="inp" data-testid="loan-simple-start" /></L>
              </div>
              <button onClick={doSimple} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-greenDeep text-white font-semibold text-sm" data-testid="loan-simple-btn">
                <Calculator className="w-4 h-4" /> Générer le tableau
              </button>
            </div>
          )}

          {mode === "calcul" && (
            <div className="rounded-2xl bg-brand-softer/60 p-4 space-y-3" data-testid="loan-calc-panel">
              <div className="grid sm:grid-cols-3 gap-3">
                <L label="Montant du prêt (€)"><input type="number" step="0.01" value={params.amount} onChange={(e) => setP("amount", e.target.value)} className="inp" data-testid="loan-amount" /></L>
                <L label="Durée (mois)"><input type="number" value={params.months} onChange={(e) => setP("months", e.target.value)} className="inp" data-testid="loan-months" /></L>
                <L label="Taux d'intérêt (% / an)"><input type="number" step="0.01" value={params.rate} onChange={(e) => setP("rate", e.target.value)} className="inp" data-testid="loan-rate" /></L>
                <L label="Taux d'assurance (% / an)"><input type="number" step="0.01" value={params.insurance_rate} onChange={(e) => setP("insurance_rate", e.target.value)} className="inp" data-testid="loan-insurance" /></L>
                <L label="Différé de remboursement">
                  <select value={params.defer_type} onChange={(e) => setP("defer_type", Number(e.target.value))} className="inp" data-testid="loan-defer-type">
                    {deferTypes.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </L>
                {deferred && <L label="Durée du différé (mois)"><input type="number" value={params.defer_months} onChange={(e) => setP("defer_months", e.target.value)} className="inp" data-testid="loan-defer-months" /></L>}
              </div>
              <button onClick={doCalculate} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-greenDeep text-white font-semibold text-sm disabled:opacity-60" data-testid="loan-calc-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />} Calculer le tableau
              </button>
            </div>
          )}

          {mode === "import" && (
            <div className="rounded-2xl bg-brand-softer/60 p-4 space-y-3" data-testid="loan-import-panel">
              <div className="flex items-start gap-3 bg-white rounded-xl border border-brand-soft p-3">
                <FileSpreadsheet className="w-5 h-5 text-brand-emerald shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-brand-greenDeep">Utilisez le modèle attendu</p>
                  <p className="text-xs text-brand-green/55">Téléchargez le fichier Excel type, complétez l'onglet « Import model » puis importez-le ci-dessous.</p>
                </div>
                <button onClick={() => window.open(`${BACKEND_URL}/api/loans/import-template`, "_blank")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-xs whitespace-nowrap transition-colors shrink-0" data-testid="loan-import-template-btn">
                  <Download className="w-3.5 h-3.5" /> Télécharger le modèle
                </button>
              </div>
              <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-brand-soft rounded-xl py-8 cursor-pointer hover:bg-white transition-colors" data-testid="loan-import-drop">
                <Upload className="w-6 h-6 text-brand-green/40" />
                <span className="text-sm text-brand-green/70 font-medium">Importer un fichier Excel (.xlsx / .xlsm)</span>
                <span className="text-xs text-brand-green/45">Onglet « Import model » — colonnes Année, Mois, N°, Mensualité, Intérêts, Assurance, Capital remboursé, Capital restant dû</span>
                <input type="file" accept=".xlsx,.xlsm,.xls" onChange={onImport} className="hidden" data-testid="loan-import-input" />
              </label>
            </div>
          )}

          {schedule && (
            <div className="border border-brand-soft rounded-2xl overflow-hidden" data-testid="loan-preview">
              <div className="px-4 py-2.5 bg-brand-lime/20 flex flex-wrap gap-x-6 gap-y-1 text-xs text-brand-greenDeep font-medium">
                <span>{totals.count} échéances</span>
                <span>Intérêts : {fmt(totals.total_interest)} €</span>
                <span>Assurance : {fmt(totals.total_insurance)} €</span>
                <span>Capital : {fmt(totals.total_principal)} €</span>
                <span className="font-bold">Coût total : {fmt(totals.total_payment)} €</span>
              </div>
              <ScheduleTable schedule={schedule} totals={totals} />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-5 py-2.5 rounded-full border border-brand-soft text-brand-green/70 font-semibold text-sm hover:bg-brand-softer">Annuler</button>
            <button onClick={save} disabled={busy || !schedule} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-50" data-testid="loan-save-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Enregistrer le prêt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-brand-green/70 mb-1.5">{label}</span>
      {children}
    </label>
  );
}


function SimulateModal({ loan, onClose, onApplied }) {
  const p = loan.params || {};
  const [mode, setMode] = useState("early_repay");
  const [extraMonth, setExtraMonth] = useState(Math.min(12, p.months || 12));
  const [extraAmount, setExtraAmount] = useState("");
  const [after, setAfter] = useState("reduce_duration");
  const [newPayment, setNewPayment] = useState("");
  const [newFrom, setNewFrom] = useState(Math.min(13, p.months || 1));
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);

  const buildBody = () => ({
    amount: p.amount, months: p.months, rate: p.rate, insurance_rate: p.insurance_rate,
    start_year: p.start_year, start_month: p.start_month, mode,
    extra_month: Number(extraMonth), extra_amount: Number(extraAmount || 0), after,
    new_payment: Number(newPayment || 0), new_payment_from: Number(newFrom),
  });

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/loans/simulate", buildBody());
      setRes(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Simulation impossible"); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!window.confirm("Appliquer cette simulation au prêt ? Le tableau d'amortissement et les échéances comptabilisées seront régénérés.")) return;
    setApplying(true);
    try {
      await api.post(`/loans/${loan.id}/apply-simulation`, buildBody());
      toast.success("Simulation appliquée — échéances régénérées");
      onApplied();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setApplying(false); }
  };

  const s = res?.savings;
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="simulate-modal">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><SlidersHorizontal className="w-5 h-5" /> Modulation d'échéance</h3>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-sm text-brand-green/55">Simulez l'impact d'un remboursement anticipé ou d'une baisse de mensualité. Simulation indicative — le prêt n'est pas modifié.</p>
          <div className="flex items-center gap-1 bg-brand-softer rounded-full p-1 w-fit" data-testid="sim-mode-tabs">
            <button onClick={() => { setMode("early_repay"); setRes(null); }} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${mode === "early_repay" ? "bg-white text-brand-greenDeep shadow-sm" : "text-brand-green/55"}`} data-testid="sim-mode-early">Remboursement anticipé</button>
            <button onClick={() => { setMode("lower_payment"); setRes(null); }} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${mode === "lower_payment" ? "bg-white text-brand-greenDeep shadow-sm" : "text-brand-green/55"}`} data-testid="sim-mode-lower">Baisse de mensualité</button>
          </div>
          {mode === "early_repay" ? (
            <div className="grid sm:grid-cols-3 gap-3">
              <L label="Montant remboursé (€)"><input type="number" value={extraAmount} onChange={(e) => setExtraAmount(e.target.value)} className="inp" data-testid="sim-extra-amount" /></L>
              <L label="À l'échéance n°"><input type="number" min="1" max={p.months} value={extraMonth} onChange={(e) => setExtraMonth(e.target.value)} className="inp" data-testid="sim-extra-month" /></L>
              <L label="Effet">
                <select value={after} onChange={(e) => setAfter(e.target.value)} className="inp" data-testid="sim-after">
                  <option value="reduce_duration">Réduire la durée</option>
                  <option value="reduce_payment">Réduire la mensualité</option>
                </select>
              </L>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <L label="Nouvelle mensualité hors assurance (€)"><input type="number" value={newPayment} onChange={(e) => setNewPayment(e.target.value)} className="inp" data-testid="sim-new-payment" /></L>
              <L label="À partir de l'échéance n°"><input type="number" min="1" max={p.months} value={newFrom} onChange={(e) => setNewFrom(e.target.value)} className="inp" data-testid="sim-new-from" /></L>
            </div>
          )}
          <button onClick={run} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-greenDeep text-white font-semibold text-sm disabled:opacity-60" data-testid="sim-run-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />} Simuler
          </button>

          {res && (
            <div className="grid sm:grid-cols-3 gap-3" data-testid="sim-results">
              <div className="rounded-2xl bg-brand-softer p-4">
                <p className="text-[11px] text-brand-green/50">Durée</p>
                <p className="font-semibold text-brand-greenDeep">{res.original.totals.count} → {res.simulated.totals.count} mois</p>
                <p className={`text-xs mt-1 font-semibold ${s.months_saved >= 0 ? "text-brand-emerald" : "text-orange-600"}`}>{s.months_saved >= 0 ? `−${s.months_saved} mois` : `+${-s.months_saved} mois`}</p>
              </div>
              <div className="rounded-2xl bg-brand-softer p-4">
                <p className="text-[11px] text-brand-green/50">Intérêts</p>
                <p className="font-semibold text-brand-greenDeep">{fmt(res.simulated.totals.total_interest)} €</p>
                <p className={`text-xs mt-1 font-semibold ${s.interest_saved >= 0 ? "text-brand-emerald" : "text-orange-600"}`}>{s.interest_saved >= 0 ? `${fmt(s.interest_saved)} € économisés` : `+${fmt(-s.interest_saved)} €`}</p>
              </div>
              <div className={`rounded-2xl p-4 ${s.total_saved >= 0 ? "bg-brand-greenDeep text-white" : "bg-orange-50"}`} data-testid="sim-total-saved">
                <p className={`text-[11px] ${s.total_saved >= 0 ? "text-white/60" : "text-orange-500"}`}>Économie totale (intérêts + assurance)</p>
                <p className={`font-semibold ${s.total_saved >= 0 ? "" : "text-orange-600"}`}>{s.total_saved >= 0 ? "" : "−"}{fmt(Math.abs(s.total_saved))} €</p>
              </div>
            </div>
          )}
          {res && (
            <div className="border border-brand-soft rounded-2xl overflow-hidden">
              <ScheduleTable schedule={res.simulated.schedule} totals={res.simulated.totals} />
            </div>
          )}
          {res && (
            <div className="flex justify-end">
              <button onClick={apply} disabled={applying} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="sim-apply-btn">
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Appliquer au prêt
              </button>
            </div>
          )}
        </div>
        <style>{`.inp{width:100%;padding:0.55rem 0.85rem;border-radius:0.8rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.85rem}.inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}`}</style>
      </div>
    </div>
  );
}

function InsuranceCompareModal({ loan, onClose, onApplied }) {
  const p = loan.params || {};
  const sched = loan.schedule || [];
  const amount = p.amount || (sched.length ? (sched[0].balance || 0) + (sched[0].principal || 0) : 0);
  const currentAnnual = loan.annual_insurance || 0;
  const currentRate = amount ? (currentAnnual / amount * 100) : (p.insurance_rate || 0);
  const monthsLeft = loan.months_left || 0;
  const [target, setTarget] = useState((currentRate * 0.6).toFixed(2));
  const [applying, setApplying] = useState(false);
  const targetAnnual = amount * Number(target || 0) / 100;
  const currentRemaining = currentAnnual / 12 * monthsLeft;
  const targetRemaining = targetAnnual / 12 * monthsLeft;
  const annualSaving = currentAnnual - targetAnnual;
  const remainingSaving = currentRemaining - targetRemaining;

  const apply = async () => {
    if (!window.confirm(`Appliquer une assurance à ${Number(target)} % au prêt ? Les échéances à venir seront recalculées.`)) return;
    setApplying(true);
    try {
      const res = await api.post(`/loans/${loan.id}/update-insurance`, { new_rate: Number(target) });
      toast.success(`Assurance mise à jour sur ${res.data.updated_rows} échéance(s) à venir`);
      onApplied();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setApplying(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="insurance-modal">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><Percent className="w-5 h-5" /> Comparateur d'assurance</h3>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-2xl bg-brand-softer p-4 text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-brand-green/60">Taux actuel</span><b>{currentRate.toFixed(2)} % / an</b></div>
            <div className="flex justify-between"><span className="text-brand-green/60">Coût actuel</span><b>{fmt(currentAnnual)} € / an</b></div>
            <div className="flex justify-between"><span className="text-brand-green/60">Reste à payer ({monthsLeft} mois)</span><b>{fmt(currentRemaining)} €</b></div>
          </div>
          <L label="Nouveau taux d'assurance visé (% / an)">
            <input type="number" step="0.01" value={target} onChange={(e) => setTarget(e.target.value)} className="inp" data-testid="insurance-target-rate" />
          </L>
          <div className="rounded-2xl bg-brand-lime/20 p-4 text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-brand-green/60">Nouveau coût</span><b>{fmt(targetAnnual)} € / an</b></div>
            <div className="flex justify-between border-t border-brand-soft pt-1.5"><span className="text-brand-green/60">Économie annuelle</span><b className={annualSaving >= 0 ? "text-brand-emerald" : "text-orange-600"} data-testid="insurance-annual-saving">{fmt(annualSaving)} €</b></div>
            <div className="flex justify-between"><span className="text-brand-green/60">Économie sur la durée restante</span><b className={remainingSaving >= 0 ? "text-brand-emerald" : "text-orange-600"} data-testid="insurance-remaining-saving">{fmt(remainingSaving)} €</b></div>
          </div>
          <p className="text-[11px] text-brand-green/50 flex items-center gap-1.5"><TrendingDown className="w-3.5 h-3.5" /> La délégation d'assurance (loi Lemoine) permet de changer d'assurance emprunteur à tout moment.</p>
          <button onClick={apply} disabled={applying} className="w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="insurance-apply-btn">
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enregistrer cette assurance
          </button>
          {(loan.insurance_history || []).length > 0 && (
            <div className="border-t border-brand-soft pt-3" data-testid="insurance-history">
              <p className="text-xs font-medium text-brand-green/60 mb-2">Historique des changements</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {[...loan.insurance_history].reverse().map((h, i) => (
                  <div key={i} className="flex items-center justify-between text-xs text-brand-green/70 px-3 py-1.5 rounded-lg bg-brand-softer/60" data-testid={`insurance-history-${i}`}>
                    <span>{new Date(h.date).toLocaleDateString("fr-FR")}</span>
                    <span className="flex items-center gap-1.5"><b>{(h.old_rate ?? 0).toFixed(2)}%</b> <TrendingDown className="w-3 h-3 text-brand-emerald" /> <b className="text-brand-greenDeep">{(h.new_rate ?? 0).toFixed(2)}%</b></span>
                    <span className="text-brand-green/50">{fmt(h.annual_after)} €/an</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <style>{`.inp{width:100%;padding:0.55rem 0.85rem;border-radius:0.8rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.85rem}.inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}`}</style>
      </div>
    </div>
  );
}
